import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  clearAccessToken,
  configureAuth,
  getLoginAt,
  getShopFloorSessionId,
  restoreSession,
} from "../api/apiClient";
import type { Role, SessionUser } from "../api/types";
import { t } from "../i18n/t";

const SESSION_MAX_MS = 8 * 60 * 60 * 1000;
const IDLE_LIMIT_MS = 30 * 60 * 1000;
const EXPIRY_WARNING_MS = 30 * 1000;

interface AuthContextValue {
  user: SessionUser | null;
  isAuthenticated: boolean;
  /** False until cookie/session restore has been attempted (avoid flash to login on reload). */
  sessionChecked: boolean;
  loading: boolean;
  notice: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
  /** Re-read the signed-in user after a self-service edit (name, phone). */
  refreshUser: () => Promise<void>;
  /**
   * Drop the CRM session in THIS browser without telling the server.
   *
   * Needed because `logout()` revokes the ShopFloorSession armed by the same
   * login (`auth.service.ts:380`) — so a kiosk asking someone to re-authenticate
   * before disarming cannot use it, or the tablet would disarm itself first and
   * make the sign-in pointless. This ends the session locally and sends them to
   * the login page; the tablet's own credential is untouched.
   */
  endCrmSessionLocally: (reason?: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const idleTimerRef = useRef<number | null>(null);
  const absoluteTimerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    if (absoluteTimerRef.current) window.clearTimeout(absoluteTimerRef.current);
    if (warningTimerRef.current) window.clearTimeout(warningTimerRef.current);
    idleTimerRef.current = null;
    absoluteTimerRef.current = null;
    warningTimerRef.current = null;
  }, []);

  /**
   * End the CRM session.
   *
   * `kioskFallback` OPTS IN to keeping an armed tablet on its kiosk screen, and
   * it is opt-in rather than automatic because the two reasons a session ends
   * are not the same thing:
   *
   *  - A TIMER decided (idle, 8-hour cap). The tablet's own credential is
   *    untouched, so it must keep working — that is the whole point of G-78/79.
   *  - A PERSON signed out. `api.logout()` REVOKES the ShopFloorSession armed by
   *    that login (`auth.service.ts:380`), so falling back to the kiosk would
   *    park them on a screen whose session no longer exists. They belong on the
   *    login page.
   *
   * Default is the safe one: /login. A caller that wants the tablet to survive
   * has to say so.
   */
  const forceLogout = useCallback(
    (reason?: string, options: { kioskFallback?: boolean } = {}) => {
      clearTimers();
      setUser(null);

      /*
       * THE CRM SESSION ENDED — THE TABLET'S HAS NOT.
       *
       * The ShopFloorSession is a separate credential with its own 12-hour
       * life, and `tablet.md` is explicit that floor mode outlives JWT₁: "JWT₁
       * expiry at hour 8 does not affect floor operations; SPA interceptor
       * stays on Enter PIN screen." The 401 path in `apiClient` already honours
       * that (`navigateToShopMode`), but these client-side timers did not — so
       * a shop tablet sitting untouched for thirty minutes left kiosk mode and
       * showed a login form. Owner-reported 2026-09-14, on the tablet.
       *
       * That is worse than an inconvenience: the G-79 order alert exists for a
       * tablet NOBODY is touching, so the feature would have stopped working
       * after the shop's first quiet half-hour, silently.
       *
       * The manager is still signed out — user cleared, JWT₁ dropped, every
       * other /app route refused by `ProtectedRoute`. Only the kiosk page
       * survives, and only because the tablet holds its own credential.
       *
       * ONE RULE, ONE PLACE: the idle timer, the 8-hour cap, a failed refresh
       * and an explicit sign-out all end a CRM session, and all of them should
       * fall back to the kiosk on an armed tablet rather than to /login.
       */
      if (options.kioskFallback && getShopFloorSessionId()) {
        // No notice: it is written for the login page, which we are not going
        // to, and it would otherwise surface at the next unrelated sign-in.
        setNotice(null);
        navigate("/app/in-the-shop", { replace: true });
        return;
      }

      if (reason) setNotice(reason);
      navigate("/login", { replace: true });
    },
    [clearTimers, navigate],
  );

  useEffect(() => {
    configureAuth({ forceLogout: () => forceLogout(t("session.ended")) });
  }, [forceLogout]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const restored = await restoreSession();
        if (!cancelled && restored) {
          setUser(restored);
        }
      } finally {
        if (!cancelled) {
          setSessionChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const armIdleTimer = useCallback(() => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => {
      forceLogout(t("session.idleSignedOut"), {
        kioskFallback: true,
      });
    }, IDLE_LIMIT_MS);
  }, [forceLogout]);

  useEffect(() => {
    if (!user) return;
    const handler = () => armIdleTimer();
    armIdleTimer();
    /*
     * `pointerdown` and `touchstart` are here because the shop runs on a
     * TABLET: `mousemove` never fires there, so before this the idle timer was
     * reset only by taps that happened to register as a `click`. A florist
     * scrolling a list with their finger or tapping a non-interactive area was
     * invisible to it.
     */
    const windowEvents: (keyof WindowEventMap)[] = [
      "mousemove",
      "pointerdown",
      "touchstart",
      "keydown",
      "click",
      "scroll",
    ];
    for (const evt of windowEvents) {
      window.addEventListener(evt, handler, { passive: true });
    }
    document.addEventListener("visibilitychange", handler);
    return () => {
      for (const evt of windowEvents) {
        window.removeEventListener(evt, handler);
      }
      document.removeEventListener("visibilitychange", handler);
    };
  }, [user, armIdleTimer]);

  useEffect(() => {
    if (!user) return;
    const loginAt = getLoginAt() ?? Date.now();
    const elapsed = Date.now() - loginAt;
    const remaining = Math.max(0, SESSION_MAX_MS - elapsed);

    if (remaining <= 0) {
      forceLogout(t("session.capReached"), {
        kioskFallback: true,
      });
      return;
    }

    const warnAt = Math.max(remaining - EXPIRY_WARNING_MS, 0);
    if (warnAt > 0) {
      warningTimerRef.current = window.setTimeout(() => {
        setNotice(t("session.expiringSoon"));
      }, warnAt);
    }
    absoluteTimerRef.current = window.setTimeout(() => {
      forceLogout(t("session.capReached"), {
        kioskFallback: true,
      });
    }, remaining);

    return () => {
      if (absoluteTimerRef.current) window.clearTimeout(absoluteTimerRef.current);
      if (warningTimerRef.current) window.clearTimeout(warningTimerRef.current);
    };
  }, [user, forceLogout]);

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      setNotice(null);
      try {
        const result = await api.login(email, password);
        setUser(result.user);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await api.logout();
    } finally {
      setLoading(false);
      forceLogout();
    }
  }, [forceLogout]);

  const endCrmSessionLocally = useCallback(
    (reason?: string) => {
      clearAccessToken();
      forceLogout(reason);
    },
    [forceLogout],
  );

  const hasRole = useCallback(
    (...roles: Role[]) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user],
  );

  const refreshUser = useCallback(async () => {
    const fresh = await api.me();
    setUser(fresh);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      sessionChecked,
      loading,
      notice,
      login,
      logout,
      hasRole,
      refreshUser,
      endCrmSessionLocally,
    }),
    [
      user,
      sessionChecked,
      loading,
      notice,
      login,
      logout,
      hasRole,
      refreshUser,
      endCrmSessionLocally,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
